import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import {
  getRouteTitle,
  isFullScreenRoute,
  shouldShowAppHeader,
} from 'helpers/appNavigation';
import { callNative } from 'helpers/nativeApp';
import {
  getContentLoadedAt,
  isAppOffline,
  setOnline,
} from 'redux/modules/nativeApp';
import AppIcon from './AppIcon';
import { APP_HEADER_ACTION_SLOT_ID } from './AppHeaderAction';
import { goBackInApp } from './appHistory';
import styles from './NativeApp.module.scss';

const DAY_MS = 24 * 60 * 60 * 1000;

/** "today at 09:14", "yesterday at 18:02", "on Thursday at 09:14" */
export function describeLoadedAt(timestamp, now = Date.now()) {
  const date = new Date(timestamp);
  const time = date.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
  });
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const daysAgo = Math.ceil((startOfToday.getTime() - date.getTime()) / DAY_MS);
  if (daysAgo <= 0) return `today at ${time}`;
  if (daysAgo === 1) return `yesterday at ${time}`;
  const day = date.toLocaleDateString('en-GB', {
    weekday: 'long',
    ...(daysAgo >= 7 ? { day: 'numeric', month: 'short' } : {}),
  });
  return `on ${day} at ${time}`;
}

/** "Showing what loaded today at 09:14." The same on every tab. */
export function getOfflineMessage(loadedAt, now) {
  return `Showing what loaded ${describeLoadedAt(loadedAt || now, now)}.`;
}

export const OfflinePill = () => (
  <span className={styles.offlinePill}>
    <AppIcon name="offline" size={13} strokeWidth={2.2} />
    Offline
  </span>
);

const OfflineBanner = () => {
  const dispatch = useDispatch();
  const loadedAt = useSelector(getContentLoadedAt);
  const [checking, setChecking] = useState(false);

  const tryAgain = async () => {
    setChecking(true);
    let connected = typeof navigator === 'undefined' || navigator.onLine;
    try {
      ({ connected } = await callNative('Network', 'getStatus'));
    } catch (e) {
      // No Network plugin in this build: navigator.onLine is the best guess.
    }
    setChecking(false);
    // Not a reload: that would drop messages and moves queued while offline.
    if (connected) dispatch(setOnline(true));
  };

  return (
    <div className={styles.offlineBanner} role="status">
      <p className={styles.offlineBannerText}>
        <strong>You&apos;re offline.</strong>{' '}
        {getOfflineMessage(loadedAt, Date.now())}
      </p>
      <button
        type="button"
        className={styles.offlineBannerButton}
        onClick={tryAgain}
        disabled={checking}
      >
        {checking ? 'Checking…' : 'Try again'}
      </button>
    </div>
  );
};

/**
 * The app's top chrome: clears the status bar / notch, shows an AppHeader
 * with a back control on pushed screens, and the offline pill and banner
 * once the connection drops. Tab roots keep their own page header.
 */
const AppTopBar = () => {
  const history = useHistory();
  const { pathname } = useLocation();
  const user = useSelector((state) => state.auth.user);
  const offline = useSelector(isAppOffline);

  if (isFullScreenRoute(pathname)) {
    return offline ? (
      <div className={styles.topBar}>
        <OfflineBanner />
      </div>
    ) : null;
  }

  const showHeader = shouldShowAppHeader(pathname, user);

  return (
    <div className={styles.topBar}>
      {showHeader ? (
        <header className={styles.appHeader}>
          <button
            type="button"
            className={styles.appHeaderBack}
            aria-label="Back"
            onClick={() => goBackInApp(history, pathname, user)}
          >
            <AppIcon name="back" size={24} strokeWidth={2.2} />
          </button>
          <h1 className={styles.appHeaderTitle}>{getRouteTitle(pathname)}</h1>
          <div className={styles.appHeaderAction}>
            {offline && <OfflinePill />}
            {/* Filled by the screen through <AppHeaderAction>. */}
            <div
              id={APP_HEADER_ACTION_SLOT_ID}
              className={styles.appHeaderActionSlot}
            />
          </div>
        </header>
      ) : (
        offline && (
          <div className={styles.topBarPillRow}>
            <OfflinePill />
          </div>
        )
      )}
      {offline && <OfflineBanner />}
    </div>
  );
};

export default AppTopBar;
